import { useState, useCallback, useMemo } from 'react';
import type { Model3D } from '@/types';

interface UseModelLoaderOptions {
  models: Model3D[];
  initialIndex?: number;
  randomStart?: boolean;
}

interface UseModelLoaderReturn {
  currentModel: Model3D;
  currentIndex: number;
  totalModels: number;
  isLoading: boolean;
  error: string | null;
  goToNext: () => void;
  goToPrevious: () => void;
  goToIndex: (index: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export function useModelLoader({
  models,
  initialIndex,
  randomStart = true,
}: UseModelLoaderOptions): UseModelLoaderReturn {
  const startIndex = useMemo(() => {
    if (initialIndex !== undefined) return initialIndex;
    if (randomStart) return Math.floor(Math.random() * models.length);
    return 0;
  }, []);

  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === models.length - 1 ? 0 : prev + 1));
    setLoading(true);
    setError(null);
  }, [models.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? models.length - 1 : prev - 1));
    setLoading(true);
    setError(null);
  }, [models.length]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < models.length) {
        setCurrentIndex(index);
        setLoading(true);
        setError(null);
      }
    },
    [models.length]
  );

  return {
    currentModel: models[currentIndex],
    currentIndex,
    totalModels: models.length,
    isLoading,
    error,
    goToNext,
    goToPrevious,
    goToIndex,
    setLoading,
    setError,
  };
}
